import {
  CoreApi,
  InteriorComponent,
  Product,
  ProductDesign,
  ProductInfoComponent,
  DoorOpeningDirectionComponent,
  Dimension
} from '@inter-ikea-storageone/range';
import {
  Entity,
  ChildComponent,
  ComponentIds,
  HierarchyUtils,
  ProductComponent,
  TransformUtils,
  Vector
} from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { ProductType } from '@inter-ikea-storageone/range';
import { getMatchingFrames } from './productUtils';
import { getProductFromEntity } from 'rangeConfig/shared/utils/getProductFromEntity';
import replaceDoors from './replaceDoors';
import * as R from 'ramda';
import { getDimensionOptions } from '../utils';
import { getReplaceableCornerSolutions } from '../../widgets/ChangeCornerSolutionParts';
import {
  getMajorityOfArray,
  isMetalInteriorEntity
} from 'rangeConfig/shared/utils/colorUtils/utils';

export const getFrameSizesInScene = (dimension: Dimension, coreApi: CoreApi): number[] => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  if (dimension === 'height') {
    return R.uniq(
      framesInScene.map(
        frame =>
          frame.getComponent<CommunicationSizeComponent>(
            ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
          )?.size?.height || 0
      )
    );
  }
  if (dimension === 'width') {
    return R.uniq(
      framesInScene.map(
        frame =>
          frame.getComponent<CommunicationSizeComponent>(
            ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
          )?.size?.width || 0
      )
    );
  }
  return R.uniq(
    framesInScene.map(
      frame =>
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size?.depth || 0
    )
  );
};

export const findMatchingCornerSolution = (
  cornerEntity: Entity,
  dimensionType: Dimension,
  size: number,
  products: Product[],
  coreApi: CoreApi
): Product | undefined => {
  const cornerProduct = getProductFromEntity(cornerEntity, products, coreApi);
  if (!cornerProduct) return;

  const cornerSolutions = getReplaceableCornerSolutions(products, cornerProduct);

  const newProduct = cornerSolutions.find(product => {
    const parts = product?.parts.map(part => products.find(p => p.coreProductId === part.id));
    const addOn = parts?.find(part => part?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM);
    const frame = parts?.find(part => part?.type === ProductType.PAX_FRAME);
    const currentProductParts = cornerProduct?.parts.map(part =>
      products.find(p => p.coreProductId === part.id)
    );
    const currentFramePart = currentProductParts.find(p => p?.type === ProductType.PAX_FRAME);
    const currentAddOnPart = currentProductParts.find(
      p => p?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM
    );

    if (!frame || !addOn || !currentFramePart || !currentAddOnPart) return false;

    const checkAddOnSize = R.equals(
      { ...currentAddOnPart?.communicatedDimensions, [dimensionType]: size },
      addOn.communicatedDimensions
    );

    const checkFrameSize = R.equals(
      { ...currentFramePart?.communicatedDimensions, [dimensionType]: size },
      frame?.communicatedDimensions
    );

    return checkAddOnSize && checkFrameSize;
  });
  return newProduct;
};

export const getHeightOfLightsInScene = (products: Product[], coreApi: CoreApi): number => {
  if (coreApi.entities.getByProductType(ProductType.PAX_OUTER_LIGHT).length === 0) return 0;

  const outerLights = products.filter(p => {
    return p.type === ProductType.PAX_OUTER_LIGHT;
  });
  const availableLightsHeight = getDimensionOptions('height')(outerLights);
  const arrLightsHeight = Object.keys(availableLightsHeight).map(i => Number(i));
  return Math.max(...arrLightsHeight);
};

export const updateOuterLightsOnFrame = (frame: Entity, oldLights: Entity[], coreApi: CoreApi) => {
  if (oldLights.length === 0) return;

  const productId = oldLights[0].getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
    ?.productRef;

  if (productId) {
    coreApi.products.addOuterLightsOnFrame(productId, frame, oldLights.length > 1, false);
  }
};

export const updateInnerLightsOnFrame = (frame: Entity, oldLights: Entity[], coreApi: CoreApi) => {
  if (oldLights.length === 0) return;

  const productId = oldLights[0].getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
    ?.productRef;

  if (productId) {
    coreApi.products.autoFillInnerLights(productId, [frame], []);
  }
};

export const changeFrameSize = (
  coreApi: CoreApi,
  matchingFrames: Product[],
  dimension: number,
  oldEntity: Entity,
  dimensionType: Dimension,
  products: Product[]
): Entity | null => {
  let newFrameEntity = null;
  // Select a frame with the new height
  const nextFrame = matchingFrames.find(
    frame => frame.communicatedDimensions[dimensionType] === dimension
  );

  if (!nextFrame) return null;

  const allowedMovementOfChildrenInMilliMeter = dimensionType === 'height' ? 1 : Number.MAX_VALUE;
  const replaceInvalidEntities = dimensionType === 'height' ? false : true;
  const oldDoorOpeningData = oldEntity.getComponent<DoorOpeningDirectionComponent>(
    DoorOpeningDirectionComponent.id
  );

  newFrameEntity = coreApi.products.replaceEntityWithHierarchy(
    oldEntity.id,
    nextFrame.coreProductId,
    replaceInvalidEntities,
    allowedMovementOfChildrenInMilliMeter
  );

  const newDoorOpeningData = newFrameEntity?.getComponent<DoorOpeningDirectionComponent>(
    DoorOpeningDirectionComponent.id
  );

  if (oldDoorOpeningData && newDoorOpeningData) {
    newDoorOpeningData.doorFrameConnectorKeys = oldDoorOpeningData.doorFrameConnectorKeys;
  }

  const changedToHigherFrame = dimensionType === 'height' && dimension === 2360;
  if (changedToHigherFrame && newFrameEntity) {
    addShelfToTopOfFrame(coreApi, newFrameEntity, products);
  }

  return newFrameEntity;
};

export const addShelfToTopOfFrame = (coreApi: CoreApi, frame: Entity, products: Product[]) => {
  const interiorsInFrame = HierarchyUtils.children(frame).filter(child =>
    child.hasComponent(InteriorComponent.id)
  );

  if (interiorsInFrame.length === 0) {
    return;
  }

  const frameSize = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
  const framePosition = TransformUtils.worldPosition(frame);
  const nonMetalInteriors = interiorsInFrame
    .filter(entity => {
      if (entity.isDisposed) return false;
      return !isMetalInteriorEntity(entity);
    })
    .filter(Boolean);

  const interiorColors = nonMetalInteriors
    .map(interior => interior.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design)
    .filter((d): d is ProductDesign => !!d);
  const newShelfColor =
    interiorColors.length > 0
      ? getMajorityOfArray(interiorColors)
      : frame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design ||
        ProductDesign.WHITE;

  const newShelfProduct = products.find(
    p =>
      p.type === ProductType.PAX_SHELF &&
      p.communicatedDimensions.width === frameSize?.width &&
      p.communicatedDimensions.depth === frameSize?.depth &&
      p.design === newShelfColor
  );
  const shelfEntity = newShelfProduct
    ? coreApi.getServices().productManager.createEntity(newShelfProduct.coreProductId)
    : undefined;
  if (shelfEntity) {
    const shelfOffset = new Vector(0, 830, 0);
    coreApi.placementService.snapToPosition(shelfEntity, framePosition.plus(shelfOffset), true);
  }
};

export const replaceFrameWithSlidingDoors = (
  slidingDoor: Entity,
  products: Product[],
  height: number,
  selectedEntity: Entity,
  coreApi: CoreApi,
  framesMatchingSelectedFrame: Product[],
  matchingSlidingDoors: { doors: Product[]; handleId?: string | undefined }[],
  matchFrameBy: Dimension,
  dimensionType: Dimension
) => {
  const parent = slidingDoor.getComponent<ChildComponent>(ComponentIds.CHILD_COMPONENT_ID)?.parent;

  if (!parent) return;

  // List of all the frames that need to be replaced
  const correspondingFramesToBeReplaced = HierarchyUtils.hierarchy(parent)
    .filter(e => e.hasComponent(ComponentIds.FRAME_COMPONENT_ID))
    .filter(e => e.id !== selectedEntity.id);

  // Delete the door entity before replacing the frames
  coreApi.entities.delete(slidingDoor);

  const lightsOnCorrespondingFrame: {
    frame: Entity;
    outerLights: Entity[];
    innerLights: Entity[];
  }[] = [];

  // Find and replace the old frames with new ones
  const newCorrespondingFrames = correspondingFramesToBeReplaced
    .map((frame, index) => {
      const frameProduct = getProductFromEntity(frame, products, coreApi);
      if (!frameProduct) return null;
      const matchingFrame = getMatchingFrames(frameProduct, products, matchFrameBy);
      if (!matchingFrame) return null;

      const outerLights = coreApi.entities.getChildrenWithProductType(
        frame,
        ProductType.PAX_OUTER_LIGHT
      );
      const innerLights = [
        ...coreApi.entities.getChildrenWithProductType(frame, ProductType.PAX_INNER_LIGHT),
        ...coreApi.entities.getChildrenWithProductType(frame, ProductType.PAX_INNER_LIGHT_OVERSIDAN)
      ];

      const newFrame = changeFrameSize(
        coreApi,
        matchingFrame,
        height,
        frame,
        dimensionType,
        products
      );

      if (newFrame) {
        lightsOnCorrespondingFrame[index] = {
          frame: newFrame,
          outerLights: outerLights,
          innerLights: innerLights
        };
      }

      return newFrame;
    })
    .filter(Boolean) as Entity[];

  const newFrameEntity = changeFrameSize(
    coreApi,
    framesMatchingSelectedFrame,
    height,
    selectedEntity,
    dimensionType,
    products
  );

  if (newFrameEntity && matchingSlidingDoors) {
    replaceDoors(coreApi, newFrameEntity, matchingSlidingDoors, true, [
      ...newCorrespondingFrames,
      newFrameEntity
    ]);
  }

  if (lightsOnCorrespondingFrame.length > 0) {
    lightsOnCorrespondingFrame.forEach(({ frame, outerLights, innerLights }) => {
      updateOuterLightsOnFrame(frame, outerLights, coreApi);
      updateInnerLightsOnFrame(frame, innerLights, coreApi);
    });
  }

  if (newFrameEntity) return newFrameEntity;
};
